import { runVehicleAudit } from './vehicleAssignmentAuditor';

/**
 * Periodic vehicle assignment consistency checker
 * Runs automatic audits at specified intervals
 */
export class VehicleAssignmentMonitor {
  private static intervalId: number | null = null;
  private static isRunning = false;
  
  /**
   * Start periodic monitoring of vehicle assignments
   * @param intervalMinutes - How often to run the audit (default: 60 minutes)
   */
  static startMonitoring(intervalMinutes: number = 60) {
    if (this.isRunning) {
      console.log('🔍 Vehicle assignment monitor is already running');
      return;
    }
    
    const intervalMs = intervalMinutes * 60 * 1000;
    
    console.log(`🔍 Starting vehicle assignment monitor (every ${intervalMinutes} minutes)`);
    
    this.intervalId = setInterval(async () => {
      try {
        console.log('🔄 Running scheduled vehicle assignment audit...');
        const result = await runVehicleAudit();
        
        if (result.fixedCount > 0) {
          console.log(`✅ Scheduled audit: Fixed ${result.fixedCount} inconsistencies`);
        } else {
          console.log('✅ Scheduled audit: All assignments consistent');
        }
      } catch (error) {
        console.error('❌ Scheduled audit failed:', error);
      }
    }, intervalMs) as unknown as number;
    
    this.isRunning = true;
  }
  
  /**
   * Stop periodic monitoring
   */
  static stopMonitoring() {
    if (this.intervalId !== null) {
      clearInterval(this.intervalId as unknown as NodeJS.Timeout);
      this.intervalId = null;
      this.isRunning = false;
      console.log('🛑 Vehicle assignment monitor stopped');
    }
  }
  
  /**
   * Check if monitoring is active
   */
  static isMonitoring() {
    return this.isRunning;
  }
  
  /**
   * Run a one-time audit check
   */
  static async runManualCheck() {
    console.log('🔍 Running manual vehicle assignment audit...');
    try {
      const result = await runVehicleAudit();
      
      if (result.fixedCount > 0) {
        console.log(`✅ Manual audit: Fixed ${result.fixedCount} inconsistencies`);
      } else {
        console.log('✅ Manual audit: All assignments consistent');
      }
      
      return result;
    } catch (error) {
      console.error('❌ Manual audit failed:', error);
      throw error;
    }
  }
}

/**
 * Quick start function for basic monitoring
 */
export const startVehicleMonitoring = (intervalMinutes?: number) => {
  VehicleAssignmentMonitor.startMonitoring(intervalMinutes);
};

/**
 * Stop monitoring function
 */
export const stopVehicleMonitoring = () => {
  VehicleAssignmentMonitor.stopMonitoring();
};
